import { ZfToggle } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocuments } from '../../api';
import type { ClientPackageDocumentDto, ClientPackageDto } from '../../api/generated/client';
import { getUpdatedClientPackageDocument } from '../../utils/clientPackageUtils';
import { ClientPackageCheckboxItem } from './ClientPackageCheckboxItem';

interface IProps {
    clientPackage: ClientPackageDto;
    disabled?: boolean;
    updateClientPackage: (updatedPackage: Partial<ClientPackageDto>) => void;
}

export const ClientPackageDocSelector: FunctionComponent<IProps> = ({ clientPackage, disabled, updateClientPackage }) => {
    const { t, i18n } = useTranslation();

    const { data: documents } = useDocuments();

    const onDocumentSelected = useCallback(
        ({ documentId }: ClientPackageDocumentDto, selected: boolean) => {
            updateClientPackage(getUpdatedClientPackageDocument(clientPackage, documentId, selected));
        },
        [clientPackage, updateClientPackage],
    );

    return (
        <div className="client-package-editor__selector-panel">
            <ZfToggle
                label={t('clientPackageDocumentsMasterToggle')}
                disabled={disabled}
                checked={clientPackage.documentsEnabled}
                onToggleChange={(event: CustomEvent<boolean>) => updateClientPackage({ documentsEnabled: event.detail })}
            />
            <div className="client-package-editor__selector-panel__content">
                {clientPackage.documentsEnabled &&
                    clientPackage.clientPackageDocuments
                        .sort((a, b) => a.documentId - b.documentId)
                        .map((document) => (
                            <ClientPackageCheckboxItem
                                key={document.documentId}
                                label={documents?.find((doc) => doc.id === document.documentId)?.name[i18n.language] ?? ''}
                                isDisabled={disabled || !clientPackage.documentsEnabled}
                                isSelected={document.selected}
                                onChange={(isChecked) => onDocumentSelected(document, isChecked)}
                            />
                        ))}
            </div>
        </div>
    );
};
